/**
 * Create Agent Page — supports 4 agent slots per client with run rotation
 */

import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { functions, db } from '../../services/firebase';
import { PortalHeader } from '../Layout/PortalHeader';

const MAX_INBOUND_PHONES = 4;
const E164_RE = /^\+[1-9]\d{1,14}$/;

interface ClientOption {
  id: string;
  name: string;
  agentSlots?: Array<{ runSlot: number; description: string; fromNumber: string; retellAgentId: string }>;
}

export function CreateAgent() {
  const navigate = useNavigate();

  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientId, setClientId] = useState('');
  // One agent can fill multiple slots — runSlots is a sorted array of 1..4.
  const [runSlots, setRunSlots] = useState<number[]>([1]);
  const [agentName, setAgentName] = useState('');
  const [voiceId, setVoiceId] = useState('cartesia-James');
  const [areaCode, setAreaCode] = useState('');
  const [flowJson, setFlowJson] = useState('');

  const [mode, setMode] = useState<'assign' | 'create' | 'edit'>('assign');
  const [existingAgentId, setExistingAgentId] = useState('');
  const [existingFromNumber, setExistingFromNumber] = useState('');
  const [description, setDescription] = useState('');

  // Edit-mode state — identifies which assignment is being edited and holds the
  // new values. editCurrentAgentId stays fixed once an assignment is picked
  // (it's the lookup key); editNewAgentId/editNewFromNumber are what the admin
  // changes. Both default to current values so single-field edits work.
  const [editCurrentAgentId, setEditCurrentAgentId] = useState('');
  const [editNewAgentId, setEditNewAgentId] = useState('');
  const [editNewFromNumber, setEditNewFromNumber] = useState('');

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // Per-client inbound calling configuration. Lives on clients/{cid}, not on
  // any agent doc — only ONE inbound agent per client, used as the override
  // for callbacks regardless of which outbound slot was originally dialled.
  const [inboundAgentId, setInboundAgentId] = useState('');
  const [inboundPhoneNumbers, setInboundPhoneNumbers] = useState<string[]>(['']);
  const [inboundLoading, setInboundLoading] = useState(false);
  const [inboundResult, setInboundResult] = useState<string | null>(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const fn = httpsCallable(functions, 'listClients');
        const res = await fn({});
        const data = res.data as { clients: ClientOption[] };
        setClients(data.clients);
      } catch (err) {
        console.error('Failed to load clients:', err);
      }
    };
    fetchClients();
  }, []);

  // Pre-load existing inbound config when admin picks a client. Read directly
  // from Firestore (admin has read access via security rules) rather than
  // round-tripping a callable — saves ~250ms.
  useEffect(() => {
    if (!clientId) {
      setInboundAgentId('');
      setInboundPhoneNumbers(['']);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'clients', clientId));
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        setInboundAgentId(typeof data.inboundAgentId === 'string' ? data.inboundAgentId : '');
        const phones = Array.isArray(data.inboundPhoneNumbers) ? data.inboundPhoneNumbers : [];
        setInboundPhoneNumbers(phones.length > 0 ? phones : ['']);
      } catch (err) {
        console.error('Failed to load client inbound config:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const updateInboundPhone = (idx: number, value: string) => {
    setInboundPhoneNumbers(prev => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const addInboundPhone = () => {
    if (inboundPhoneNumbers.length >= MAX_INBOUND_PHONES) return;
    setInboundPhoneNumbers(prev => [...prev, '']);
  };

  const removeInboundPhone = (idx: number) => {
    setInboundPhoneNumbers(prev => prev.length === 1 ? [''] : prev.filter((_, i) => i !== idx));
  };

  const handleSaveInboundConfig = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId) {
      setInboundResult('Error: Pick a client first.');
      return;
    }
    const cleanedPhones = inboundPhoneNumbers
      .map(p => p.trim())
      .filter(p => p.length > 0);
    for (const p of cleanedPhones) {
      if (!E164_RE.test(p)) {
        setInboundResult(`Error: ${p} is not a valid E.164 phone (must start with + and country code).`);
        return;
      }
    }
    if (cleanedPhones.length > MAX_INBOUND_PHONES) {
      setInboundResult(`Error: Max ${MAX_INBOUND_PHONES} inbound phones per client.`);
      return;
    }

    setInboundLoading(true);
    setInboundResult(null);
    try {
      const fn = httpsCallable(functions, 'setInboundAgent');
      await fn({
        clientId,
        inboundAgentId: inboundAgentId.trim() || null,
        inboundPhoneNumbers: cleanedPhones,
      });
      setInboundResult(
        inboundAgentId.trim()
          ? `Saved: agent ${inboundAgentId.trim()} bound to ${cleanedPhones.length} inbound phone${cleanedPhones.length === 1 ? '' : 's'}.`
          : 'Cleared: inbound agent unassigned and any previously bound phones unbound.',
      );
    } catch (err) {
      setInboundResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setInboundLoading(false);
    }
  };

  const selectedClient = clients.find(c => c.id === clientId);
  const existingSlots = selectedClient?.agentSlots || [];

  // For the assign form, the "current agent" is identified by retellAgentId. Slots
  // already held by THIS agent are pre-checked and editable; slots held by OTHER
  // agents are disabled (server would reject the conflict anyway). For the create
  // form, no agent ID exists yet, so all currently-occupied slots are "other".
  // For slot pre-check logic, "this agent" means the one we're about to write
  // to: the typed input in assign mode, the picked assignment in edit mode,
  // nothing in create mode.
  const currentRetellAgentId = mode === 'assign'
    ? existingAgentId.trim()
    : mode === 'edit'
      ? editCurrentAgentId.trim()
      : '';
  const slotsHeldByThisAgent = new Set(
    existingSlots.filter(a => a.retellAgentId === currentRetellAgentId).map(a => a.runSlot),
  );
  const slotsHeldByOtherAgent = new Map<number, string>();
  for (const s of existingSlots) {
    if (s.retellAgentId !== currentRetellAgentId) {
      slotsHeldByOtherAgent.set(s.runSlot, s.description || s.retellAgentId);
    }
  }

  // Auto-select sensible defaults whenever the client or target agent changes.
  // - Editing an existing agent → pre-check the slots it already holds.
  // - New agent / new assignment → first available slot, falling back to slot 1.
  useEffect(() => {
    if (!clientId) return;
    if (slotsHeldByThisAgent.size > 0) {
      setRunSlots(Array.from(slotsHeldByThisAgent).sort((a, b) => a - b));
      return;
    }
    for (let s = 1; s <= 4; s++) {
      if (!slotsHeldByOtherAgent.has(s)) { setRunSlots([s]); return; }
    }
    setRunSlots([1]); // All taken — admin will see the conflict on submit.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, currentRetellAgentId]);

  const toggleSlot = (slot: number) => {
    if (slotsHeldByOtherAgent.has(slot)) return; // disabled
    setRunSlots(prev => {
      const next = prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot];
      return next.sort((a, b) => a - b);
    });
  };

  const handleAssignAgent = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId || !existingAgentId || !existingFromNumber) return;
    if (runSlots.length === 0) {
      setResult('Error: Pick at least one slot.');
      return;
    }
    setLoading(true);
    setResult(null);

    try {
      const fn = httpsCallable(functions, 'assignAgent');
      const res = await fn({
        clientId,
        retellAgentId: existingAgentId,
        fromNumber: existingFromNumber,
        runSlots,
        description: description || undefined,
      });
      const data = res.data as { message: string };
      setResult(`${data.message} (Slot${runSlots.length > 1 ? 's' : ''} ${runSlots.join(', ')})`);
      setTimeout(() => navigate('/admin'), 2000);
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setLoading(false);
    }
  };

  // Pick an existing assignment to edit. Fills the form with its current values
  // so a single-field edit (e.g. only the phone) just works without re-typing.
  const pickAssignmentToEdit = (slot: { runSlot: number; description: string; fromNumber: string; retellAgentId: string }) => {
    setEditCurrentAgentId(slot.retellAgentId);
    setEditNewAgentId(slot.retellAgentId);
    setEditNewFromNumber(slot.fromNumber || '');
    setDescription(slot.description || '');
    setRunSlots([slot.runSlot]);
    setResult(null);
  };

  const handleEditAssignment = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId || !editCurrentAgentId) return;
    if (runSlots.length === 0) {
      setResult('Error: Pick at least one slot.');
      return;
    }
    setLoading(true);
    setResult(null);

    try {
      const fn = httpsCallable(functions, 'updateAgentAssignment');
      const res = await fn({
        clientId,
        currentRetellAgentId: editCurrentAgentId,
        newRetellAgentId: editNewAgentId.trim() !== editCurrentAgentId ? editNewAgentId.trim() : undefined,
        newFromNumber: editNewFromNumber.trim(),
        runSlots,
        description: description || undefined,
      });
      const data = res.data as { message: string };
      setResult(data.message);
      setTimeout(() => navigate('/admin'), 2000);
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (e: FormEvent) => {
    e.preventDefault();
    if (!clientId || !agentName) return;
    setLoading(true);
    setResult(null);

    try {
      const fn = httpsCallable(functions, 'createRetellAgent');
      const res = await fn({
        clientId,
        agentName,
        voiceId,
        conversationFlowJson: flowJson || undefined,
        purchasePhoneAreaCode: areaCode || undefined,
      });
      const data = res.data as { message: string; agentId?: string; phoneNumber?: string };
      setResult(data.message);
      // Auto-assign to slot(s) if agent was created
      if (data.agentId) {
        try {
          const assignFn = httpsCallable(functions, 'assignAgent');
          await assignFn({
            clientId,
            retellAgentId: data.agentId,
            fromNumber: data.phoneNumber || '',
            runSlots,
            description: agentName,
          });
          setResult(`${data.message} — assigned to Slot${runSlots.length > 1 ? 's' : ''} ${runSlots.join(', ')}`);
        } catch { /* Agent created but slot assign failed — not critical */ }
      }
      setTimeout(() => navigate('/admin'), 3000);
    } catch (err) {
      setResult('Error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PortalHeader showCredits={false} />

      <div className="container" style={{ maxWidth: '700px' }}>
        {/* Mode Selector */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          <button onClick={() => setMode('assign')} className={`btn ${mode === 'assign' ? 'btn-primary' : 'btn-secondary'}`}>
            Assign Existing Agent
          </button>
          <button onClick={() => setMode('create')} className={`btn ${mode === 'create' ? 'btn-primary' : 'btn-secondary'}`}>
            Create New Agent
          </button>
          <button onClick={() => setMode('edit')} className={`btn ${mode === 'edit' ? 'btn-primary' : 'btn-secondary'}`}>
            Edit Assignment
          </button>
        </div>

        {/* Client Selection */}
        <div className="form-group">
          <label>Client *</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)} required>
            <option value="">-- Select Client --</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Inbound Calling Configuration — per-client. ONE inbound agent +
            up to 4 dedicated inbound phones. Assigning an inbound agent does
            NOT enable inbound for the user — they must opt in via the toggle
            on their dashboard (with credit-deduction confirmation). The
            admin override toggle on ManageClient can force-disable. */}
        {clientId && (
          <form onSubmit={handleSaveInboundConfig} style={{
            marginBottom: '24px',
            padding: '16px',
            border: '1px solid var(--dark-3)',
            borderRadius: '8px',
            background: 'rgba(221,177,84,0.04)',
          }}>
            <h3 style={{ marginTop: 0, color: 'var(--gold)' }}>
              Inbound Calling Configuration (per-client)
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-400)', marginTop: 0 }}>
              One inbound agent applies across all this client's outbound agents
              for callbacks. Up to {MAX_INBOUND_PHONES} dedicated inbound phones may
              also be bound to ring this agent directly.
            </p>
            <div className="form-group">
              <label>Inbound Retell Agent ID</label>
              <input
                type="text"
                value={inboundAgentId}
                onChange={e => setInboundAgentId(e.target.value)}
                placeholder="agent_xxxxxxxxxxxxxxxxxxxx (leave blank to clear)"
              />
            </div>
            <div className="form-group">
              <label>Dedicated Inbound Phone Numbers (E.164, up to {MAX_INBOUND_PHONES})</label>
              {inboundPhoneNumbers.map((phone, idx) => (
                <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                  <input
                    type="text"
                    value={phone}
                    onChange={e => updateInboundPhone(idx, e.target.value)}
                    placeholder="+12145551234"
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => removeInboundPhone(idx)}
                    className="btn btn-secondary"
                    style={{ padding: '6px 12px' }}
                    disabled={inboundPhoneNumbers.length === 1 && !phone}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {inboundPhoneNumbers.length < MAX_INBOUND_PHONES && (
                <button
                  type="button"
                  onClick={addInboundPhone}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '0.85rem' }}
                >
                  + Add Phone
                </button>
              )}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={inboundLoading}
            >
              {inboundLoading ? 'Saving...' : 'Save Inbound Config'}
            </button>
            {inboundResult && (
              <div style={{
                marginTop: '12px', padding: '10px 14px', borderRadius: '6px',
                background: inboundResult.startsWith('Error') ? 'var(--red-bg)' : 'var(--green-bg)',
                color: inboundResult.startsWith('Error') ? 'var(--red)' : 'var(--green)',
                fontSize: '0.85rem',
              }}>{inboundResult}</div>
            )}
          </form>
        )}

        {/* Run Slot Selection — multi-select. One agent + phone can fill any
            number of slots, so all the runs in those slots use the same voice
            instead of rotating to a different agent. */}
        <div className="form-group">
          <label>Agent Slots (1-4) — pick one or more *</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {[1, 2, 3, 4].map(slot => {
              const isChecked = runSlots.includes(slot);
              const heldByOther = slotsHeldByOtherAgent.get(slot);
              const heldByThis = slotsHeldByThisAgent.has(slot);
              const disabled = !!heldByOther;
              const borderColor = isChecked ? 'var(--gold)' : 'var(--dark-3)';
              const bg = disabled
                ? 'rgba(255,255,255,0.02)'
                : (isChecked ? 'rgba(221,177,84,0.1)' : 'var(--dark)');
              const labelColor = disabled
                ? 'var(--gray-500)'
                : (isChecked ? 'var(--gold)' : 'var(--gray-300)');
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={() => toggleSlot(slot)}
                  disabled={disabled}
                  title={heldByOther ? `Slot ${slot} is used by ${heldByOther}. Unassign that agent first.` : undefined}
                  style={{
                    padding: '12px 8px',
                    border: `2px solid ${borderColor}`,
                    borderRadius: '8px',
                    background: bg,
                    color: labelColor,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    textAlign: 'center',
                    fontFamily: 'inherit',
                    fontSize: '0.85rem',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{isChecked ? '☑' : '☐'} Slot {slot}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                    Runs {slot}, {slot + 4}
                  </div>
                  {heldByThis && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--gold-dark)', marginTop: '4px' }}>
                      this agent
                    </div>
                  )}
                  {heldByOther && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                      used by {heldByOther.slice(0, 16)}
                    </div>
                  )}
                  {!heldByThis && !heldByOther && (
                    <div style={{ fontSize: '0.7rem', color: 'var(--green)', marginTop: '4px' }}>
                      empty
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {mode === 'assign' ? (
          <form onSubmit={handleAssignAgent}>
            <h2 className="section-title">
              Assign Existing Retell Agent to Slot{runSlots.length > 1 ? 's' : ''} {runSlots.join(', ')}
            </h2>
            <div className="form-group">
              <label>Retell Agent ID *</label>
              <input type="text" value={existingAgentId} onChange={e => setExistingAgentId(e.target.value)} required placeholder="agent_xxxxxxxxxxxxxxxxxxxx" />
            </div>
            <div className="form-group">
              <label>From Phone Number (E.164) *</label>
              <input type="text" value={existingFromNumber} onChange={e => setExistingFromNumber(e.target.value)} required placeholder="+12145551234" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Agent name or deal" />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn btn-primary" disabled={loading || !clientId || runSlots.length === 0}>
                {loading
                  ? 'Assigning...'
                  : `Assign to Slot${runSlots.length > 1 ? 's' : ''} ${runSlots.join(', ')}`}
              </button>
              <Link to="/admin" className="btn btn-secondary">Cancel</Link>
            </div>
          </form>
        ) : mode === 'edit' ? (
          <form onSubmit={handleEditAssignment}>
            <h2 className="section-title">Edit Existing Assignment</h2>

            {/* Step 1: pick which assignment to edit. Hidden once one is picked. */}
            {!editCurrentAgentId && (
              <div className="form-group">
                <label>Select assignment to edit</label>
                {existingSlots.length === 0 ? (
                  <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                    {clientId ? 'No agents assigned to this client yet.' : 'Pick a client above first.'}
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {existingSlots.map(s => (
                      <button
                        key={`${s.retellAgentId}-${s.runSlot}`}
                        type="button"
                        onClick={() => pickAssignmentToEdit(s)}
                        style={{
                          padding: '12px 14px', border: '1px solid var(--dark-3)', borderRadius: '8px',
                          background: 'var(--dark)', color: 'var(--white)', textAlign: 'left',
                          cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.9rem',
                        }}
                      >
                        <div style={{ fontWeight: 700, color: 'var(--gold)' }}>
                          Slot {s.runSlot} — {s.description || s.retellAgentId}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--gray-400)', marginTop: '4px' }}>
                          {s.retellAgentId} · {s.fromNumber || '(no phone)'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: editable form once a slot is picked. */}
            {editCurrentAgentId && (
              <>
                <div className="form-group">
                  <label>Currently editing</label>
                  <div style={{
                    padding: '10px 14px', borderRadius: '6px', background: 'var(--dark)',
                    border: '1px solid var(--dark-3)', fontFamily: 'monospace', fontSize: '0.85rem',
                    color: 'var(--gray-300)',
                  }}>
                    {editCurrentAgentId}
                  </div>
                </div>
                <div className="form-group">
                  <label>New Retell Agent ID *</label>
                  <input
                    type="text" value={editNewAgentId}
                    onChange={e => setEditNewAgentId(e.target.value)}
                    required placeholder="agent_xxxxxxxxxxxxxxxxxxxx"
                  />
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                    Leave unchanged to keep the same agent. Change to swap (the old agent's webhook + GCS entry will be cleaned up automatically).
                  </div>
                </div>
                <div className="form-group">
                  <label>New From Phone Number (E.164) *</label>
                  <input
                    type="text" value={editNewFromNumber}
                    onChange={e => setEditNewFromNumber(e.target.value)}
                    required placeholder="+12145551234"
                  />
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '4px' }}>
                    The phone's outbound_agent_id will be re-bound to the final agent automatically.
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input type="text" value={description} onChange={e => setDescription(e.target.value)} placeholder="Agent name or deal" />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="submit" className="btn btn-primary"
                    disabled={loading || !clientId || !editNewAgentId || !editNewFromNumber || runSlots.length === 0}
                  >
                    {loading
                      ? 'Saving...'
                      : `Save Changes to Slot${runSlots.length > 1 ? 's' : ''} ${runSlots.join(', ')}`}
                  </button>
                  <button
                    type="button" className="btn btn-secondary"
                    onClick={() => { setEditCurrentAgentId(''); setEditNewAgentId(''); setEditNewFromNumber(''); setDescription(''); }}
                  >
                    Pick Different Slot
                  </button>
                  <Link to="/admin" className="btn btn-secondary">Cancel</Link>
                </div>
              </>
            )}
          </form>
        ) : (
          <form onSubmit={handleCreateAgent}>
            <h2 className="section-title">
              Create New Agent for Slot{runSlots.length > 1 ? 's' : ''} {runSlots.join(', ')}
            </h2>
            <div className="form-group">
              <label>Agent Name *</label>
              <input type="text" value={agentName} onChange={e => setAgentName(e.target.value)} required placeholder="Company Name - Deal Name" />
            </div>
            <div className="form-group">
              <label>Voice</label>
              <select value={voiceId} onChange={e => setVoiceId(e.target.value)}>
                <option value="cartesia-James">James (Cartesia)</option>
                <option value="11labs-Max">Max (11Labs)</option>
                <option value="cartesia-Julia">Julia (Cartesia)</option>
                <option value="11labs-Susan">Susan (11Labs)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Phone Area Code (optional — purchases a new number)</label>
              <input type="text" value={areaCode} onChange={e => setAreaCode(e.target.value)} placeholder="214" maxLength={3} />
            </div>
            <div className="form-group">
              <label>Conversation Flow JSON (optional)</label>
              <textarea value={flowJson} onChange={e => setFlowJson(e.target.value)}
                placeholder="Paste conversation flow JSON" rows={6}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--dark-3)', borderRadius: '6px',
                  background: 'var(--dark)', color: 'var(--white)', fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn btn-primary" disabled={loading || !clientId || !agentName || runSlots.length === 0}>
                {loading
                  ? 'Creating...'
                  : `Create & Assign to Slot${runSlots.length > 1 ? 's' : ''} ${runSlots.join(', ')}`}
              </button>
              <Link to="/admin" className="btn btn-secondary">Cancel</Link>
            </div>
          </form>
        )}

        {result && (
          <div style={{
            marginTop: '24px', padding: '16px', borderRadius: '8px',
            background: result.startsWith('Error') ? 'var(--red-bg)' : 'var(--green-bg)',
            color: result.startsWith('Error') ? 'var(--red)' : 'var(--green)',
            border: `1px solid ${result.startsWith('Error') ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`
          }}>{result}</div>
        )}
      </div>
    </div>
  );
}
